import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Laptop, Sparkles } from 'lucide-react';
import { Modal } from './ui/Modal';
import { setPlanModalOpen } from '../store/uiModalStore';
import './Vault.css';

/**
 * "Your free cloud vault is full" (Standalone vault card U1). Two ways on:
 * upgrade (the app's plans card), or keep THIS item on this device only — it
 * is saved here with sync off and never pushed. Local features are never
 * gated; nothing already stored is blocked.
 *
 * Props: isOpen, cap ({ items, max }), count (items the card is about; 1 =
 * "this one"), onKeepOnDevice (async), onClose, closeOnKeep (default true;
 * false when onKeepOnDevice settles the card itself).
 */
export default function VaultCapModal({ isOpen, cap, count = 1, onKeepOnDevice, onClose, closeOnKeep = true }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const max = cap?.max ?? 50;

  const keep = async () => {
    setBusy(true);
    setError('');
    try {
      await onKeepOnDevice();
      if (closeOnKeep) onClose();
    } catch (err) {
      // Plans v2: keeping it here can still be refused by the plan's local
      // cap (a 2nd host or DB connection on Free); its sentence says why.
      setError(err?.planLimit && err.message ? err.message : t('vault.capKeepFailed', 'That did not work. Try again.'));
    }
    setBusy(false);
  };

  const upgrade = () => {
    onClose();
    setPlanModalOpen(true);
  };

  return (
    <Modal isOpen={isOpen} onClose={() => !busy && onClose()} size="md" title={t('vault.capTitle', 'Your free cloud vault is full')}>
      <div className="vault-pass-body" data-testid="vault-cap">
        <p className="vault-pass-text">
          {count > 1
            ? t('vault.capBodyMany', 'The Free plan keeps {{max}} items in your encrypted cloud vault. Upgrade for unlimited, or keep these {{count}} items on this device only.', { max, count })
            : t('vault.capBody', 'The Free plan keeps {{max}} items in your encrypted cloud vault. Upgrade for unlimited, or keep this one on this device only.', { max })}
        </p>
        <button type="button" className="vault-cloud-choice" disabled={busy} onClick={upgrade}>
          <Sparkles size={18} />
          <span className="vault-cloud-choice-texts">
            <span className="vault-cloud-choice-title">{t('vault.capUpgrade', 'Upgrade for unlimited items')}</span>
            <span className="vault-pass-hint">{t('vault.capUpgradeHelp', 'Every item on all your devices and in the browser extension.')}</span>
          </span>
        </button>
        <button type="button" className="vault-cloud-choice" disabled={busy} onClick={keep}>
          <Laptop size={18} />
          <span className="vault-cloud-choice-texts">
            <span className="vault-cloud-choice-title">
              {count > 1 ? t('vault.capKeepMany', 'Keep them on this device only') : t('vault.capKeep', 'Keep it on this device only')}
            </span>
            <span className="vault-pass-hint">
              {t(
                'vault.capKeepHelp',
                'Saved on this computer, not uploaded. Other devices and the browser extension will not see it until you upload it from Settings → Vault after an upgrade.'
              )}
            </span>
          </span>
        </button>
        {error && <div className="vault-modal-error">{error}</div>}
      </div>
    </Modal>
  );
}
